'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import { Listbox, ListboxItem } from 'shared/ui/Listbox'
import type { ListboxProps } from 'shared/ui/Listbox/Listbox'
import type { ListboxItemProps } from 'shared/ui/Listbox/ListboxItem'
import { ZIndex } from 'shared/ui/types'

import { getListboxPosition } from './utils'

import styles from './SelectList.module.scss'

type SizeType = 'medium' | 'large'

export type SelectOptionType = {
  label: string | React.ReactNode
  value: string
}

interface SelectListProps
  extends Omit<React.HTMLAttributes<HTMLUListElement>, 'onSelect'> {
  anchorRef: React.RefObject<HTMLButtonElement | null>
  isOpen: boolean
  multiple: boolean
  size: SizeType
  variant: 'primary' | 'ghost'
  options: SelectOptionType[]
  id: string
  selectedValues: string | string[]
  style?: React.CSSProperties
  onSelect: (value: string) => void
  className?: string
  wrapperRef?: React.RefObject<HTMLDivElement | null>
  onClose: () => void
  ref?: React.RefObject<HTMLUListElement | null>
}

const SelectList = ({
  anchorRef,
  isOpen,
  onClose,
  selectedValues,
  multiple,
  options,
  id,
  size,
  variant,
  onSelect,
  className,
  style,
  wrapperRef,
  ref,
  onBlur,
  ...props
}: SelectListProps) => {
  const internalWrapperRef = useRef<HTMLDivElement>(null)
  const listboxWrapperRef = wrapperRef || internalWrapperRef
  const [isMeasured, setIsMeasured] = useState(false)

  const { register, unregister, getZIndex } = useOverlayStack()

  useEffect(() => {
    if (isOpen) {
      register({ id, type: 'dropdown' })
    } else {
      unregister(id)
    }
  }, [isOpen, register, unregister, id])

  const handleSelect = useCallback(
    (optionId: string) => {
      onSelect(optionId)
    },
    [onSelect]
  )

  useEffect(() => {
    if (!isOpen) {
      setIsMeasured(false)

      return
    }

    const updatePosition = () => {
      const position = getListboxPosition(anchorRef, listboxWrapperRef)

      if (position && listboxWrapperRef.current) {
        listboxWrapperRef.current.style.transform = `translate(${position.left}px, 0)`
        listboxWrapperRef.current.style.top = `${position.top}px`

        if (!isMeasured) {
          setIsMeasured(true)
        }
      }
    }

    // Initial position calculation with RAF to ensure element is rendered
    requestAnimationFrame(() => {
      updatePosition()
    })

    const handleResize = () => {
      updatePosition()
    }

    // For scroll, update immediately by delta without RAF for instant response
    window.addEventListener('scroll', handleResize, true)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleResize, true)
      window.removeEventListener('resize', handleResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLUListElement>) => {
      const relatedTarget = e.relatedTarget as Node | null
      const wrapper = wrapperRef?.current

      // Only close if focus is moving outside the listbox wrapper and not to the anchor button
      if (
        wrapper &&
        relatedTarget &&
        !wrapper.contains(relatedTarget) &&
        !anchorRef.current?.contains(relatedTarget) &&
        relatedTarget !== anchorRef.current
      ) {
        onClose()
      }
      onBlur?.(e)
    },
    [wrapperRef, anchorRef, onBlur, onClose]
  )

  const zIndex = useMemo(
    () => getZIndex(id) || ZIndex.DROPDOWN,
    [getZIndex, id]
  )

  const minWidth = anchorRef.current?.offsetWidth

  return (
    <div
      ref={listboxWrapperRef}
      className={clsx(
        styles.listWrapper,
        {
          [styles.listWrapperOpen]: isOpen,
          [styles.listWrapperGhost]: variant === 'ghost'
        },
        className
      )}
      style={
        {
          '--select-list-min-width': minWidth ? `${minWidth}px` : undefined,
          '--select-list-z-index': String(zIndex),
          ...style
        } as React.CSSProperties
      }
      role='listbox'
    >
      <StyledListbox
        id={id}
        selectedOptionId={selectedValues}
        onSelect={handleSelect}
        ref={ref}
        onBlur={handleBlur}
        $size={size}
        {...props}
      >
        {options.map(option => {
          const isSelected =
            typeof selectedValues === 'string'
              ? selectedValues === option.value
              : selectedValues.includes(option.value)

          return (
            <StyledListboxItem
              key={`${option.value}`}
              optionId={option.value}
              $selected={isSelected}
              $size={size}
              $multiple={multiple ?? false}
              value={option.value}
              $minWidth={minWidth}
            >
              {multiple && (
                <CheckboxIndicator $selected={isSelected} aria-hidden='true'>
                  {isSelected ? '✓' : ''}
                </CheckboxIndicator>
              )}
              {option.label}
            </StyledListboxItem>
          )
        })}
      </StyledListbox>
    </div>
  )
}

SelectList.displayName = 'SelectList'

export default SelectList

type StyledListboxProps = ListboxProps & {
  $size: SizeType
}

const StyledListbox = ({
  $size,
  className,
  ...props
}: StyledListboxProps) => (
  <Listbox {...props} className={clsx(styles.listbox, className)} />
)

type StyledListboxItemProps = ListboxItemProps & {
  $selected: boolean
  $size: SizeType
  $multiple: boolean
  value: string
  $minWidth: number | undefined
}

const StyledListboxItem = ({
  $selected,
  $size,
  $multiple,
  $minWidth,
  className,
  style,
  ...props
}: StyledListboxItemProps) => (
  <ListboxItem
    {...props}
    className={clsx(
      styles.option,
      $size === 'medium' ? styles.optionMedium : styles.optionLarge,
      {
        [styles.optionMultiple]: $multiple,
        [styles.optionSelected]: $selected
      },
      className
    )}
    style={
      $minWidth
        ? ({
            ...style,
            '--select-option-min-width': `${$minWidth}px`
          } as React.CSSProperties)
        : style
    }
  />
)

type CheckboxIndicatorProps = React.HTMLAttributes<HTMLSpanElement> & {
  $selected: boolean
}

const CheckboxIndicator = ({
  $selected,
  className,
  ...props
}: CheckboxIndicatorProps) => (
  <span
    {...props}
    className={clsx(
      styles.checkbox,
      { [styles.checkboxSelected]: $selected },
      className
    )}
  />
)
